import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { Icon } from '@/components/common/Icon';

import type { ReactNode } from 'react';

export interface TooltipProps {
  direction?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
  content: ReactNode;
  id?: string;
  children?: ReactNode;
}

/**
The tool tips do not show on mobile.
*/

export const Tooltip = ({
  children,
  content,
  className,
  direction = 'right',
  id,
}: TooltipProps) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  let tooltipId = useRef(id || Math.random().toString()).current;
  if (process.env.NODE_ENV === 'test') tooltipId = '1';

  useEffect(() => {
    const closeTooltip = (e) => {
      if (tooltipVisible && e.keyCode === 27) {
        setTooltipVisible(false);
      }
    };
    window.addEventListener('keydown', closeTooltip);
    return () => window.removeEventListener('keydown', closeTooltip);
  }, [tooltipVisible]);

  return (
    <Wrapper
      className={className}
      tabIndex={0}
      onFocus={() => setTooltipVisible(true)}
      onBlur={() => setTooltipVisible(false)}
      tooltipVisible={tooltipVisible}
    >
      <TooltipIconWrapper
        onMouseEnter={() => setTooltipVisible(true)}
        onMouseLeave={() => setTooltipVisible(false)}
      >
        <StyledIcon
          size="23px"
          icon="InfoCircle"
          aria-describedby={`tooltip-${tooltipId}`}
        />
        {children}
      </TooltipIconWrapper>
      <Content
        id={`tooltip-${tooltipId}`}
        direction={direction}
        aria-hidden={!tooltipVisible}
        role="tooltip"
      >
        {content}
      </Content>
    </Wrapper>
  );
};

const Wrapper = styled.div<{ tooltipVisible: boolean }>`
  ${screen.md} {
    display: inline;
  }
  display: none;
  position: relative;
  overflow-x: ${(props) => (props.tooltipVisible ? 'unset' : 'hidden')};
`;

const StyledIcon = styled(Icon)`
  margin-right: var(--spacing-100);
`;

const TooltipIconWrapper = styled.span`
  display: flex;
  align-items: center;
`;

type ContentProps = Pick<TooltipProps, 'direction'>;

const Content = styled.div<ContentProps>`
  font-size: var(--font-size-xx-small);
  background: var(--colour-white);
  color: var(--color-navy);
  font-weight: var(--font-weight-normal);
  z-index: 1;

  display: inline;
  position: absolute;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  border: 0;

  &[aria-hidden='false'] {
    height: auto;
    margin: auto;
    padding: 20px;
    width: 275px;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.25);
    overflow: visible;
    top: 50%;
    transform: translateY(-50%);

    &:after {
      top: 50%;
      transform: translateY(-50%) rotate(45deg);
    }
    ${({ direction }) =>
      direction === 'right' &&
      css`
        left: 100%;
        margin-left: 0.5rem;

        &:after {
          left: -6px;
          box-shadow: -3px 3px 4px rgb(0 0 0 / 25%);
        }
      `}

    ${({ direction }) =>
      direction === 'left' &&
      css`
        margin-right: 0.5rem;
        right: 100%;

        &:after {
          right: -6px;
          box-shadow: 3px -3px 4px rgb(0 0 0 / 25%);
        }
      `}
  }

  &:after {
    display: block;
    content: '';
    position: absolute;
    width: 12px;
    height: 12px;
    background: var(--colour-white);
    transform-origin: center;
  }
`;
